/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useRef, useState } from 'react';

import { Form, Row, Col, Button, UploadFileType } from 'components';

// Using Upload from antd intentionally, our custom upload component
// is implemented incorrectly
import { Upload, InputRef } from 'antd';

import { getPhoneInputMask, isValidUrl } from 'utils';

import fileStackClient from '@app/filestack';
import { InputFile } from 'filestack-js';
import {
  PickerResponse,
  PickerFileMetadata,
} from 'filestack-js/build/main/lib/picker';

import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { AuthContext } from 'contexts';
import { OnboardingOrgDetails } from './OnboardingOrgDetails';
import {
  OnboardingMemberNumbers,
  MLSNumberView,
} from './OnboardingMemberNumbers';
import {
  OrgLocalOnboardingProfile,
  UserLocalOnboardingProfile,
} from '../types';
import { StyledInput } from './common';

export type OnboardingCompleteProfileFormValues =
  | UserLocalOnboardingProfile
  | (Omit<UserLocalOnboardingProfile, 'org'> & {
      org: OrgLocalOnboardingProfile;
    });

export type OnboardingCompleteProfileFormProps = {
  loading?: boolean;
  initialValues: OnboardingCompleteProfileFormValues;
  isLicensed: boolean;
  onSubmit: (values: OnboardingCompleteProfileFormValues) => void;
  onEmailError?: () => void;
};

const API_KEY = 'AVsLFCmooSeCfsjDRz3Cbz';
const BASE_URL = 'https://cdn.filestackcontent.com';

export const OnboardingCompleteProfileForm: React.FC<
  OnboardingCompleteProfileFormProps
> = props => {
  const { initialValues, loading = false, onSubmit, onEmailError } = props;

  const [avatarFilestackId, setAvatarFilestackId] = useState<
    string | undefined
  >(initialValues.avatarFilestackId);
  const [avatarFullFilestackId, setAvatarFullFilestackId] = useState<
    string | undefined
  >(initialValues.avatarFullFilestackId);

  const [avatarFile, setAvatarFile] = useState<UploadFileType>();

  const [mlsNumbers, setMlsNumbers] = useState(initialValues.mls_numbers);

  const [emailError, setEmailError] = useState('');

  const _handleSubmit = async (values: OnboardingCompleteProfileFormValues) => {
    onSubmit({
      ...values,
      avatarFilestackId,
      avatarFullFilestackId,
      ...(mlsNumbers && { mls_numbers: mlsNumbers }),
    });
  };
  const _showAvatarPicker = () => {
    // Keep uncropped avatar in scope here for when upload is done
    let avatarFull: PickerFileMetadata;

    const handleFileSelected = async (file: PickerFileMetadata) => {
      // store the full avatar for later (handleFileUploaded)
      if (file.source === 'url') {
        if (!isValidUrl(file.originalPath)) {
          throw new Error('Invalid URL format. Please try again.');
        }
        avatarFull = (await fileStackClient.storeURL(
          file.originalPath,
        )) as PickerFileMetadata;
      } else {
        avatarFull = await fileStackClient.upload(
          file.originalFile as InputFile,
          undefined,
          { altText: 'user avatar' },
        );
      }
    };

    const handleFileUploaded = async (results: PickerResponse) => {
      const [file] = results.filesUploaded;
      setAvatarFilestackId(results.filesUploaded[0].handle);
      setAvatarFullFilestackId(avatarFull.handle);
      setAvatarFile({
        uid: file.key,
        name: file.filename,
        status: 'done',
        url: file.url,
        thumbUrl: file.url,
        size: file.size,
        type: file.mimetype,
      });
    };

    const opts = {
      accept: 'image/*',
      maxSize: 1024 * 1024 * 2,
      uploadInBackground: false,
      exposeOriginalFile: true,
      fromSources: ['local_file_system', 'url'],
      transformations: { rotate: true, crop: { aspectRatio: 1, force: true } },
      onFileSelected: handleFileSelected,
      onUploadDone: handleFileUploaded,
    };

    const picker = fileStackClient.picker(opts);
    picker.open();
  };

  const phoneRef = useRef<InputRef>();
  useEffect(() => {
    if (phoneRef?.current?.input) {
      getPhoneInputMask().mask(phoneRef.current.input);
    }
  }, [phoneRef?.current?.input]);

  useEffect(() => {
    if (initialValues.avatarFilestackId) {
      const resize = `resize=width:48,height:48`;

      setAvatarFile({
        uid: initialValues.avatarFilestackId,
        name: 'uploaded_profile_picture',
        status: 'done',
        url: `${BASE_URL}/${initialValues.avatarFilestackId}`,
        thumbUrl: `${BASE_URL}/${API_KEY}/${resize}/${initialValues.avatarFilestackId}`,
        size: 0, // unimportant field
        type: '', // unimportant field
      });
    }
  }, [initialValues.avatarFilestackId]);

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Form
      name="onboardingUpdateProfileForm"
      initialValues={initialValues}
      layout="vertical"
      onFinish={_handleSubmit}
    >
      <Form.Item name="photo" label="Profile Photo">
        {avatarFile ? (
          <Upload
            openFileDialogOnClick={false}
            listType="picture"
            fileList={[avatarFile]}
            onRemove={() => setAvatarFile(undefined)}
          />
        ) : (
          <div onClick={_showAvatarPicker}>
            <Upload openFileDialogOnClick={false} listType="picture-card">
              {uploadButton}
            </Upload>
          </div>
        )}
      </Form.Item>

      <StyledInput.Group>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[
                { required: true, message: 'Please enter your first name' },
              ]}
            >
              <StyledInput disabled={loading} placeholder="First Name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[
                { required: true, message: 'Please enter your last name' },
              ]}
            >
              <StyledInput disabled={loading} placeholder="Last Name" />
            </Form.Item>
          </Col>
        </Row>
      </StyledInput.Group>
      <Form.Item
        name="email"
        label="Email"
        rules={[
          {
            required: true,
            message: 'Please enter a valid email',
            type: 'email',
          },
        ]}
        {...(emailError && { validateStatus: 'error', help: emailError })}
      >
        <StyledInput
          disabled={!!initialValues.email || loading}
          placeholder="Email"
          onChange={() => {
            setEmailError('');
          }}
        />
      </Form.Item>
      <Form.Item
        name="phone"
        label="Mobile Number"
        rules={[
          { required: true, message: 'This must be a valid phone number' },
        ]}
      >
        <StyledInput disabled={loading} ref={phoneRef as any} />
      </Form.Item>

      <Form.Item>
        <OnboardingOrgDetails profile={initialValues} />
      </Form.Item>

      {mlsNumbers?.length ? (
        <Form.Item label="Member Number(s)">
          <OnboardingMemberNumbers
            mlsNumbers={mlsNumbers}
            onPrimaryChange={setMlsNumbers}
          />
        </Form.Item>
      ) : initialValues.licenseNumber ? (
        <Form.Item label="Member Number(s)">
          <MLSNumberView
            item={{ board: 'No Board', number: initialValues.licenseNumber }}
          />
        </Form.Item>
      ) : null}

      <Button
        type="primary"
        block
        htmlType="submit"
        disabled={loading}
        loading={loading}
      >
        Next
      </Button>
    </Form>
  );
};
